import { useState } from 'react';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { ApiError } from '../../../types/api';
import { errorMessage } from '../../../utils/apiResponse';
import { Dialog } from './AccountBits';
import type { NurseryCustomer } from '../../../types/nursery';

/**
 * Deleting a customer entered by mistake (Accounts Admin, Rob 2026-10-20).
 * The server deletes one nothing has been done with, its addresses and
 * contacts going with it. One with an order, invoice, payment or site is
 * refused (409 CUSTOMER_IN_USE, naming what it has): the dialog then offers
 * to make it inactive, which keeps its paperwork and takes it off the lists.
 */
export default function DeleteCustomerDialog({ customer, onClose, onDeleted, onDeactivated }: {
  customer: Pick<NurseryCustomer, 'id' | 'name' | 'active'>;
  onClose: () => void;
  onDeleted: () => void;
  onDeactivated: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The server's sentence when it is in use, and so can only be made inactive.
  const [inUse, setInUse] = useState<string | null>(null);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.deleteCustomerPermanently(customer.id);
      if (res.success) return onDeleted();
      setError(errorMessage(res, 'Could not delete the customer'));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'CUSTOMER_IN_USE') setInUse(e.message);
      else setError(errorMessage(e, 'Could not delete the customer'));
    }
    setBusy(false);
  };

  const deactivate = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.updateCustomer(customer.id, { active: false });
      if (res.success) return onDeactivated();
      setError(errorMessage(res, 'Could not make the customer inactive'));
    } catch (e) {
      setError(errorMessage(e, 'Could not make the customer inactive'));
    }
    setBusy(false);
  };

  return (
    <Dialog
      title={inUse ? 'This customer is in use' : 'Delete customer'}
      subtitle={customer.name}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          {inUse
            ? customer.active !== false && (
              <Button onClick={deactivate} disabled={busy}>{busy ? 'Working…' : 'Make inactive'}</Button>
            )
            : <Button variant="danger" onClick={remove} disabled={busy}>{busy ? 'Working…' : 'Delete for good'}</Button>}
        </>
      }
    >
      {inUse ? (
        <p className="text-sm text-neutral-700 break-words">
          {inUse}
          {customer.active === false && ' It is already inactive.'}
        </p>
      ) : (
        <p className="text-sm text-neutral-700 break-words">
          For a customer entered by mistake, such as a second copy of one you already have. It is removed with its
          addresses and contacts, and cannot be brought back. A customer with orders, invoices or payments cannot be
          deleted; you will be offered to make it inactive instead.
        </p>
      )}
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
    </Dialog>
  );
}
